import { useState } from 'react';
import { Drawer, Descriptions, Select, Switch, Button, Popconfirm, Space, Tag, Form, Input, Divider, Image, Upload, Alert, App } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { updateAppUser, deleteAppUser, uploadQrCode, ROLE_NAME, type AppUser } from '@/api/appUsers';

const roleOptions = Object.entries(ROLE_NAME).map(([v, l]) => ({ value: Number(v), label: l }));

// Fixed test / App-Store-review account (backend default TEST_ACCOUNT_EMAIL).
// Its no-email fixed-code login is gated on is_active, so the Active switch
// below is the way to enable/disable it. Keep in sync if the env is overridden.
const TEST_ACCOUNT_EMAIL = 'test@test.com';

interface MloInfo { name: string; phone: string; email: string; nmls_id: string; dre_number_ca: string; qr_code_url: string }
interface RealtorInfo { agent_name: string; company_name: string }
const EMPTY_MLO: MloInfo = { name: '', phone: '', email: '', nmls_id: '', dre_number_ca: '', qr_code_url: '' };
const EMPTY_REALTOR: RealtorInfo = { agent_name: '', company_name: '' };

// qr_code_url is rendered specially (paste + upload + preview); the rest are plain inputs.
const MLO_FIELDS: { key: keyof MloInfo; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'nmls_id', label: 'NMLS ID' },
  { key: 'dre_number_ca', label: 'DRE Number (CA)' },
];

/** Structured, editable profile matching the iOS "edit personal profile" editor. */
function ProfileForm({ user }: { user: AppUser }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const p = user.profile ?? {};
  const [mlo, setMlo] = useState<MloInfo>({ ...EMPTY_MLO, ...((p.mlo_info as Partial<MloInfo>) ?? {}) });
  const [realtor, setRealtor] = useState<RealtorInfo>({ ...EMPTY_REALTOR, ...((p.realtor_info as Partial<RealtorInfo>) ?? {}) });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Preserve any non-standard top-level keys (e.g. `lang`) on save.
  const otherKeys = Object.fromEntries(Object.entries(p).filter(([k]) => k !== 'mlo_info' && k !== 'realtor_info'));
  const otherEntries = Object.entries(otherKeys);

  const setM = (k: keyof MloInfo, v: string) => setMlo((s) => ({ ...s, [k]: v }));

  // Upload a picked QR image → attachments endpoint → set qr_code_url.
  // customRequest bypasses antd's default XHR so we go through apiUpload (auth).
  const uploadQr: UploadProps['customRequest'] = async ({ file }) => {
    const f = file as File;
    if (!f.type.startsWith('image/')) { message.error('Please choose an image file'); return; }
    if (f.size > 20 * 1024 * 1024) { message.error('Image must be under 20 MB'); return; }
    setUploading(true);
    try {
      const url = await uploadQrCode(user.id, f);
      setM('qr_code_url', url);
      message.success('QR uploaded — click "Save profile" to persist');
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  async function save() {
    setSaving(true);
    try {
      await updateAppUser(user.id, { profile: { ...otherKeys, mlo_info: mlo, realtor_info: realtor } });
      message.success('Profile saved');
      qc.invalidateQueries({ queryKey: ['app-users'] });
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Divider orientation="left" style={{ marginTop: 24 }}>MLO Info</Divider>
      <Form layout="vertical" size="small">
        {MLO_FIELDS.map((f) => (
          <Form.Item key={f.key} label={f.label} style={{ marginBottom: 10 }}>
            <Input value={mlo[f.key]} onChange={(e) => setM(f.key, e.target.value)} />
          </Form.Item>
        ))}
        <Form.Item label="QR Code" style={{ marginBottom: 10 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              value={mlo.qr_code_url}
              placeholder="Paste a URL or upload an image"
              onChange={(e) => setM('qr_code_url', e.target.value)}
            />
            <Upload accept="image/*" showUploadList={false} customRequest={uploadQr}>
              <Button icon={<UploadOutlined />} loading={uploading}>Upload</Button>
            </Upload>
          </Space.Compact>
          {mlo.qr_code_url ? <Image src={mlo.qr_code_url} alt="QR" width={120} style={{ marginTop: 8 }} /> : null}
        </Form.Item>

        <Divider orientation="left">Realtor Info</Divider>
        <Form.Item label="Realtor Name" style={{ marginBottom: 10 }}>
          <Input value={realtor.agent_name} onChange={(e) => setRealtor((s) => ({ ...s, agent_name: e.target.value }))} />
        </Form.Item>
        <Form.Item label="Realtor Company" style={{ marginBottom: 10 }}>
          <Input value={realtor.company_name} onChange={(e) => setRealtor((s) => ({ ...s, company_name: e.target.value }))} />
        </Form.Item>
      </Form>

      {otherEntries.length > 0 && (
        <Descriptions title="Other" column={1} size="small" style={{ marginTop: 8 }}
          items={otherEntries.map(([k, v]) => ({ key: k, label: k, children: String(v) }))} />
      )}

      <Button type="primary" onClick={save} loading={saving} style={{ marginTop: 8 }}>Save profile</Button>
    </>
  );
}

export function UserDetailDrawer({ user, onClose }: { user: AppUser | null; onClose: () => void }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['app-users'] });

  if (!user) return null;

  const isTestAccount = (user.email ?? '').toLowerCase() === TEST_ACCOUNT_EMAIL;

  const patch = async (body: Parameters<typeof updateAppUser>[1]) => {
    try {
      await updateAppUser(user.id, body);
      message.success('Saved');
      refresh();
    } catch (e) {
      message.error((e as Error).message);
    }
  };

  return (
    <Drawer
      open
      title={<Space>{user.email}{isTestAccount && <Tag color="gold">Test / review account</Tag>}</Space>}
      width={520}
      onClose={onClose}
    >
      {isTestAccount && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Fixed test / App-Store-review account"
          description="Logs in with a hardcoded code and no email. Toggle Active off below to deactivate its login; toggle on to re-enable."
        />
      )}
      <Descriptions
        column={1}
        size="small"
        bordered
        items={[
          { key: 'id', label: 'ID', children: user.id },
          { key: 'u', label: 'Username', children: user.username ?? '—' },
          { key: 'status', label: 'Status', children: <Tag color={user.is_active ? 'success' : 'default'}>{user.is_active ? 'active' : 'inactive'}</Tag> },
          { key: 'c', label: 'Created', children: new Date(user.created_at).toLocaleString() },
        ]}
      />

      <div style={{ margin: '16px 0', display: 'grid', gap: 12 }}>
        <Space>
          <span style={{ width: 90, display: 'inline-block' }}>Role</span>
          <Select style={{ width: 200 }} value={user.role_id} options={roleOptions} onChange={(v) => patch({ role_id: v })} />
        </Space>
        <Space>
          <span style={{ width: 90, display: 'inline-block' }}>Subscribed</span>
          <Switch checked={user.is_subscribed} onChange={(v) => patch({ is_subscribed: v })} />
        </Space>
        <Space>
          <span style={{ width: 90, display: 'inline-block' }}>Active</span>
          <Switch checkedChildren="active" unCheckedChildren="inactive" checked={user.is_active} onChange={(v) => patch({ is_active: v })} />
        </Space>
      </div>

      <ProfileForm key={user.id} user={user} />

      <div style={{ marginTop: 24 }}>
        <Popconfirm
          title={`Delete ${user.email}?`}
          onConfirm={async () => {
            try {
              await deleteAppUser(user.id);
              message.success('Deleted');
              refresh();
              onClose();
            } catch (e) {
              message.error((e as Error).message);
            }
          }}
        >
          <Button danger>Delete user</Button>
        </Popconfirm>
      </div>
    </Drawer>
  );
}
